import { makeAutoObservable } from 'mobx'

const ALL_COLUMNS = [
	{ id: 'select', title: '', subtitle: '' }, // checkbox column (locked)
	{ id: 'student', title: 'Student', subtitle: '' }, // locked

	{ id: 'grade', title: 'Grade', subtitle: 'Class 12' },
	{ id: 'country', title: 'Country', subtitle: '' },
	{ id: 'tags', title: 'Tags', subtitle: '“Needs Help”, “On-Track”' },
	{ id: 'course', title: 'Course', subtitle: '' },
	{ id: 'counselor', title: 'Counselor', subtitle: '' },
	{ id: 'documents', title: 'Documents', subtitle: 'SOP, LOR, Transcripts' },
]

const LOCKED_COLUMN_IDS = ['select', 'student']

const DEFAULT_ACTIVE_COLUMN_IDS = [
	...LOCKED_COLUMN_IDS,
	'grade',
	'country',
	// 'tags',
	'course',
	'counselor',
]

const DEFAULT_INACTIVE_COLUMN_IDS = ['documents']

const pickByIds = (ids = []) => {
	const map = new Map(ALL_COLUMNS.map(c => [c.id, c]))
	return ids.map(id => map.get(id)).filter(Boolean)
}

const normalizeLockedFirst = (active = []) => {
	const map = new Map(active.map(c => [c.id, c]))
	const locked = LOCKED_COLUMN_IDS.map(id => map.get(id)).filter(Boolean)
	const rest = active.filter(c => !LOCKED_COLUMN_IDS.includes(c.id))
	return [...locked, ...rest]
}

class StudentsFiltersStore {
	isOpen = false

	filters = {
		studentNationality: [], // ['US', 'DE'] - used for country of application
		grade: [], // ['Grade 12']
		assignmentStatus: [], // ['SOP Review', 'LOR Review']
		course: [], // ['Science', 'Commerce'] - renamed from stream
	}

	activeColumns = normalizeLockedFirst(pickByIds(DEFAULT_ACTIVE_COLUMN_IDS))
	inactiveColumns = pickByIds(DEFAULT_INACTIVE_COLUMN_IDS)

	constructor() {
		makeAutoObservable(this, {}, { autoBind: true })
	}

	open() {
		this.isOpen = true
	}

	close() {
		this.isOpen = false
	}

	toggle() {
		this.isOpen = !this.isOpen
	}

	setFilter(key, values) {
		this.filters[key] = Array.isArray(values) ? values : []
	}

	clearFilter(key) {
		this.filters[key] = []
	}

	removeFilterValue(key, value) {
		this.filters[key] = (this.filters[key] || []).filter(v => v !== value)
	}

	get hasAnyFilters() {
		return Object.values(this.filters).some(arr => (arr || []).length > 0)
	}

	// ---- Columns actions ----
	moveToInactive(id) {
		if (LOCKED_COLUMN_IDS.includes(id)) return

		const item = this.activeColumns.find(c => c.id === id)
		if (!item) return

		this.activeColumns = normalizeLockedFirst(
			this.activeColumns.filter(c => c.id !== id)
		)

		if (!this.inactiveColumns.some(c => c.id === id)) {
			this.inactiveColumns = [item, ...this.inactiveColumns]
		}
	}

	moveToActive(id) {
		const item = this.inactiveColumns.find(c => c.id === id)
		if (!item) return

		this.inactiveColumns = this.inactiveColumns.filter(c => c.id !== id)

		if (!this.activeColumns.some(c => c.id === id)) {
			this.activeColumns = normalizeLockedFirst([...this.activeColumns, item])
		}
	}

	reorderActive(from, to) {
		if (from === to) return
		if (from < 0 || to < 0) return
		if (from >= this.activeColumns.length || to >= this.activeColumns.length)
			return

		const lockedCount = LOCKED_COLUMN_IDS.length
		if (from < lockedCount || to < lockedCount) return

		const next = [...this.activeColumns]
		const [moved] = next.splice(from, 1)
		if (!moved) return

		next.splice(to, 0, moved)
		this.activeColumns = normalizeLockedFirst(next)
	}

	resetColumns() {
		this.activeColumns = normalizeLockedFirst(
			pickByIds(DEFAULT_ACTIVE_COLUMN_IDS)
		)
		this.inactiveColumns = pickByIds(DEFAULT_INACTIVE_COLUMN_IDS)
	}

	isLockedColumn(id) {
		return LOCKED_COLUMN_IDS.includes(id)
	}

	get lockedColumnIds() {
		return LOCKED_COLUMN_IDS
	}
}

export const studentsFiltersStore = new StudentsFiltersStore()
